import React, { useState, useCallback, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar/Navbar';
import LeftSidebar from './components/LeftSidebar/LeftSidebar';
import RightSidebar from './components/RightSidebar/RightSidebar';
import DashboardTemplatePicker from './components/DashboardTemplatePicker/DashboardTemplatePicker';
import CollegeListPage from './pages/CollegeListPage';
import ProgressPage from './pages/ProgressPage';
import HomePage from './pages/HomePage';
import MyInfoPage from './pages/MyInfoPage.js';
import AccountPage from './pages/AccountPage';
import SupportPage from './pages/SupportPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ApplicationTimelinePage from './pages/ApplicationTimelinePage';
import EssayGamePlanPage from './pages/EssayGamePlanPage';
import CollegeListGuidePage from './pages/CollegeListGuidePage';
import DashboardTemplateGuidePage from './pages/DashboardTemplateGuidePage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ProtectedRoute from './components/ProtectedRoute';
import FirstDashboardTour from './components/FirstDashboardTour/FirstDashboardTour';
import CalendarPage from './pages/CalendarPage';
import WelcomePage from './pages/WelcomePage';
import { clearDashboardStorage, usePersistedState } from './helpers/useDashboardStorage';
import { CUSTOM_DASHBOARD_TEMPLATE, FINAL_LIST_PREFILL_TEMPLATE_IDS, getDashboardTemplate } from './helpers/dashboardTemplates';
import { fetchCollegeData } from './helpers/collegeAPI';
import { smartSearchCollegeDatabase } from './helpers/searchCollegeDatabase';
import { lookupFafsaCode } from './helpers/fafsaLookup';
import { readManualProgressColleges } from './helpers/progressSource';
import { applyCustomTheme, readCustomTheme, subscribeToCustomTheme } from './helpers/customTheme';
import ToastContainer from './components/Toast/Toast';
import { useAuth } from './context/AuthContext';
import { useThemeMode } from './context/ThemeModeContext';
import { userStorage } from './lib/userStorage';

const shouldPromptForFinalList = (templateId) => FINAL_LIST_PREFILL_TEMPLATE_IDS.includes(templateId);

async function createDashboardRowsFromFinalList() {
  const finalColleges = readManualProgressColleges();
  if (!finalColleges.length) return [];

  try {
    const collegeData = await fetchCollegeData();
    if (!collegeData?.results?.length) return [];

    const rows = [];
    const seen = new Set();

    for (const collegeName of finalColleges) {
      try {
        const schoolData = await smartSearchCollegeDatabase(collegeName, collegeData);
        const resolvedName = schoolData?.['latest.school.name'];
        if (!resolvedName || seen.has(resolvedName)) continue;

        seen.add(resolvedName);
        const fafsaResult = lookupFafsaCode(resolvedName);
        const customFields = {};
        if (fafsaResult) customFields.custom_fafsa_code = fafsaResult.code;

        rows.push({
          id: Date.now() + rows.length,
          schoolData: { ...schoolData },
          category: '',
          date: '',
          checked: false,
          customFields,
        });
      } catch {
        // Skip colleges that cannot be resolved cleanly.
      }
    }

    return rows;
  } catch {
    return [];
  }
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { darkMode, toggleDarkMode } = useThemeMode();
  const [dashboardTemplatePickerOpen, setDashboardTemplatePickerOpen] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = usePersistedState('onboarding_complete', false);
  const { user } = useAuth();
  const [dashboards, setDashboards] = usePersistedState('app_dashboards', [
    { id: 'dashboard-1', name: 'Dashboard 1', path: '/dashboard/dashboard-1' },
  ]);

  // One-time migration: remove old Home dashboard if it exists
  useEffect(() => {
    const hasHome = dashboards.some((d) => d.id === 'home' && d.path === '/');
    if (hasHome) {
      const filtered = dashboards.filter((d) => d.id !== 'home');
      if (filtered.length === 0) {
        filtered.push({ id: 'dashboard-1', name: 'Dashboard 1', path: '/dashboard/dashboard-1' });
      }
      setDashboards(filtered);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useNavigate();
  const location = useLocation();

  // Find the active dashboard based on current path
  const activeDashboard = dashboards.find((d) => d.path === location.pathname);
  const activeDashboardId = activeDashboard ? activeDashboard.id : null;
  const orderedDashboards = [...dashboards].sort((a, b) => {
    if (Boolean(a.pinned) === Boolean(b.pinned)) return 0;
    return a.pinned ? -1 : 1;
  });

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const toggleNotifications = useCallback(() => {
    setNotificationsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    applyCustomTheme(readCustomTheme());
    return subscribeToCustomTheme(applyCustomTheme);
  }, []);

  // Warm the shared college dataset after the signed-in shell is responsive.
  // Dropdowns reuse the same pending request or seven-day device cache.
  useEffect(() => {
    if (!user) return undefined;

    const warmCollegeData = () => {
      void fetchCollegeData().catch(() => {
        // Dropdowns retain their existing retry and error states.
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(warmCollegeData, { timeout: 1500 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timerId = window.setTimeout(warmCollegeData, 750);
    return () => window.clearTimeout(timerId);
  }, [user]);

  useEffect(() => {
    if (user && !onboardingComplete) {
      setOnboardingComplete(true);
    }
  }, [onboardingComplete, setOnboardingComplete, user]);

  const handleSelectDashboard = useCallback(() => {
    // sidebar stays open — only closes via the icon button
  }, []);

  const getUniqueDashboardName = useCallback((baseName) => {
    const existingNames = new Set(dashboards.map((dashboard) => dashboard.name));
    if (!existingNames.has(baseName)) return baseName;

    let index = 2;
    while (existingNames.has(`${baseName} ${index}`)) {
      index += 1;
    }
    return `${baseName} ${index}`;
  }, [dashboards]);

  const getNextDashboardNumber = useCallback(() => {
    const usedNumbers = new Set(
      dashboards
        .map((dashboard) => {
          const match = String(dashboard.id || '').match(/^dashboard-(\d+)$/);
          return match ? Number(match[1]) : null;
        })
        .filter((number) => Number.isInteger(number) && number > 0)
    );

    let number = 1;
    while (usedNumbers.has(number)) {
      number += 1;
    }
    return number;
  }, [dashboards]);

  const handleAddDashboard = useCallback(() => {
    setDashboardTemplatePickerOpen(true);
  }, []);

  const handleCreateDashboardFromTemplate = useCallback(async (templateId, options = {}) => {
    const template = getDashboardTemplate(templateId);
    const isCustom = template.id === CUSTOM_DASHBOARD_TEMPLATE.id;
    const dashboardNumber = getNextDashboardNumber();
    const newDashboard = {
      id: `dashboard-${dashboardNumber}`,
      name: getUniqueDashboardName(isCustom ? `Dashboard ${dashboardNumber}` : template.name),
      path: `/dashboard/dashboard-${dashboardNumber}`,
      pinned: false,
    };
    const importedData = options.importData || null;
    const prefilledRows = importedData?.rows || (options.prefillFinalList ? await createDashboardRowsFromFinalList() : []);
    clearDashboardStorage(newDashboard.id);
    userStorage.setItem(`dashboard_${newDashboard.id}_activeColumns`, JSON.stringify(importedData?.activeColumns || template.activeColumns));
    userStorage.setItem(`dashboard_${newDashboard.id}_customColumns`, JSON.stringify(importedData?.customColumns || template.customColumns));
    userStorage.setItem(`dashboard_${newDashboard.id}_rows`, JSON.stringify(prefilledRows));
    userStorage.setItem(`dashboard_${newDashboard.id}_sortConfig`, JSON.stringify(null));
    userStorage.setItem(`dashboard_${newDashboard.id}_columnFilters`, JSON.stringify({}));
    userStorage.setItem(`dashboard_${newDashboard.id}_columnWidths`, JSON.stringify(importedData?.columnWidths || template.columnWidths || {}));
    userStorage.setItem(`dashboard_${newDashboard.id}_frozenSchoolName`, JSON.stringify(false));
    setDashboards((prev) => [...prev, newDashboard]);
    setDashboardTemplatePickerOpen(false);
    navigate(newDashboard.path);
  }, [getNextDashboardNumber, getUniqueDashboardName, navigate, setDashboards]);

  const handleRenameDashboard = useCallback((id, newName) => {
    setDashboards((prev) =>
      prev.map((d) => (d.id === id ? { ...d, name: newName } : d))
    );
  }, [setDashboards]);

  const handleTogglePinDashboard = useCallback((id) => {
    setDashboards((prev) =>
      prev.map((d) => (d.id === id ? { ...d, pinned: !d.pinned } : d))
    );
  }, [setDashboards]);

  const handleDeleteDashboard = useCallback((id) => {
    if (dashboards.length <= 1) return;

    const dashboardToDelete = dashboards.find((d) => d.id === id);
    const remaining = dashboards.filter((d) => d.id !== id);
    clearDashboardStorage(id);
    setDashboards(remaining);

    if (dashboardToDelete && location.pathname === dashboardToDelete.path) {
      const nextDashboard = remaining.find((d) => d.pinned) || remaining[0];
      if (nextDashboard) {
        navigate(nextDashboard.path);
      }
    }
  }, [dashboards, location.pathname, navigate, setDashboards]);

  // Derive current dashboard name for the CollegeListPage title
  const currentDashboardName = activeDashboard ? activeDashboard.name : 'College List';

  // Auth pages render standalone (no navbar / sidebars)
  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/signin' ||
    location.pathname === '/sign-in' ||
    location.pathname === '/signup';

  if (isAuthPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signin" element={<Login />} />
        <Route path="/sign-in" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
      </Routes>
    );
  }

  const isLegalPage = ['/privacy', '/terms'].includes(location.pathname);
  if (isLegalPage) {
    return (
      <Routes>
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
    );
  }

  const isPublicInfoPage = ['/about', '/support', '/contact'].includes(location.pathname);

  if (!onboardingComplete && isPublicInfoPage) {
    return (
      <Routes>
        <Route path="/about" element={<AboutPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
    );
  }

  const isPublicGuidePage = [
    '/application-timeline',
    '/essay-game-plan',
    '/college-list-guide',
    '/dashboard-template-guide',
  ].includes(location.pathname);

  if (!user && isPublicGuidePage) {
    return (
      <Routes>
        <Route path="/application-timeline" element={<ApplicationTimelinePage />} />
        <Route path="/essay-game-plan" element={<EssayGamePlanPage />} />
        <Route path="/college-list-guide" element={<CollegeListGuidePage />} />
        <Route path="/dashboard-template-guide" element={<DashboardTemplateGuidePage />} />
      </Routes>
    );
  }

  // Default redirect target: Home gives users their next step before they jump into a list.
  const defaultPath = '/home';

  // The welcome screen belongs only to the public root landing page. Other
  // private deep links must reach ProtectedRoute so Login can preserve them.
  if (!onboardingComplete && !user && location.pathname === '/') {
    return <WelcomePage />;
  }

  // Otherwise show main app interface
  return (
    <div className="App">
      <ToastContainer sidebarOpen={notificationsOpen} />
      <Navbar
        onToggleSidebar={toggleSidebar}
        onToggleNotifications={toggleNotifications}
        onToggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
      />

      <LeftSidebar
        isOpen={sidebarOpen}
        dashboards={orderedDashboards}
        activeDashboardId={activeDashboardId}
        onSelectDashboard={handleSelectDashboard}
        onAddDashboard={handleAddDashboard}
        onRenameDashboard={handleRenameDashboard}
        onTogglePinDashboard={handleTogglePinDashboard}
        onDeleteDashboard={handleDeleteDashboard}
      />

      <RightSidebar isOpen={notificationsOpen} />

      {user && (
        <FirstDashboardTour
          user={user}
          dashboards={orderedDashboards}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
      )}

      {dashboardTemplatePickerOpen && (
        <DashboardTemplatePicker
          finalListCount={readManualProgressColleges().length}
          onClose={() => setDashboardTemplatePickerOpen(false)}
          onSelectTemplate={handleCreateDashboardFromTemplate}
          shouldPromptForFinalList={shouldPromptForFinalList}
        />
      )}

      <div className={`app-content ${sidebarOpen ? 'app-content--shifted-left' : ''} ${notificationsOpen ? 'app-content--shifted-right' : ''}`}>
        <Routes>
          {/* Root redirects to first dashboard */}
          <Route path="/" element={<Navigate to={defaultPath} replace />} />
          <Route path="/dashboard" element={<Navigate to={orderedDashboards[0]?.path || '/dashboard/dashboard-1'} replace />} />

          {/* Home page */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomePage dashboards={dashboards} />
              </ProtectedRoute>
            }
          />

          {/* Dynamic dashboards — each gets a CollegeListPage */}
          <Route
            path="/dashboard/:dashboardId"
            element={
              <ProtectedRoute>
                <CollegeListPage dashboardName={currentDashboardName} />
              </ProtectedRoute>
            }
          />

          {/* My Info page */}
          <Route
            path="/my-info"
            element={
              <ProtectedRoute>
                <MyInfoPage />
              </ProtectedRoute>
            }
          />

          {/* Account page */}
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />

          {/* Support page */}
          <Route
            path="/about"
            element={<AboutPage />}
          />

          <Route
            path="/support"
            element={<SupportPage />}
          />

          {/* Contact page */}
          <Route
            path="/contact"
            element={<ContactPage />}
          />

          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Application timeline page */}
          <Route
            path="/application-timeline"
            element={<ApplicationTimelinePage />}
          />

          <Route
            path="/essay-game-plan"
            element={<EssayGamePlanPage />}
          />

          <Route
            path="/college-list-guide"
            element={<CollegeListGuidePage />}
          />

          <Route
            path="/dashboard-template-guide"
            element={<DashboardTemplateGuidePage />}
          />

          {/* Progress page */}
          <Route
            path="/progress"
            element={
              <ProtectedRoute>
                <ProgressPage dashboards={dashboards} />
              </ProtectedRoute>
            }
          />

          {/* Calendar page */}
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
        </Routes>

        <footer className="app-footer">
          <span>© 2026 MaiColleges</span>
          <nav aria-label="MaiColleges footer links">
            <a href="/about">About Us</a>
            <a href="/support">Support</a>
            <a href="/contact">Contact Us</a>
            <a href="https://docs.google.com/forms/d/e/1FAIpQLSeeM81wlBlaGlwaO5zZCo-3X0wP0wKe5kMuGodEffWcIXSx1w/viewform?usp=publish-editor" target="_blank" rel="noreferrer">Bug &amp; Feature Form</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </nav>
        </footer>
      </div>
    </div>
  );
}

export default App;
